/* ───────────────────────────────────────────────────────────────────────────
 * lcsInference.js — deterministic, oilfield-flavored commentary for one lease.
 *
 * Pure function. No fetches. No randomness.
 * Designed to be drop-in-replaceable by a live LLM call later: the output
 * shape matches the COMMENTARY_CONTRACT below, with `source: "deterministic_local"`.
 * If you wire Anthropic later, set `source: "llm"`.
 *
 * Input — any combination of these is accepted; missing fields degrade gracefully:
 *   {
 *     leaseId, leaseName, rank, score, assetType,
 *     status, recommendation, confidence,
 *     actualRows, forecastRows, streamsPresent,
 *     recent: { oilAvg, gasAvg, waterAvg, oilTrend, gasTrend, waterTrend },
 *     metrics: { mape, byStream: { Oil: {mape}, Gas: {mape}, Water: {mape} } },
 *     signals: { declineRisk, volatility, anomalyCount, dataQuality },
 *     economics: { pv, cashFlow, netback, breakeven, decision } | null,
 *     modelInfo: { ageDays, modelVersion },
 *     caveats: [string]
 *   }
 *
 * Output — the COMMENTARY_CONTRACT.
 * ──────────────────────────────────────────────────────────────────────── */

export const COMMENTARY_CONTRACT_VERSION = "1.0.0";

// Fields the deterministic inference reads. Surfaced in `inputFieldsUsed`
// so a future LLM-backed implementation can list the same provenance.
const INPUT_FIELDS = [
  "leaseId", "assetType", "status", "recommendation", "confidence",
  "actualRows", "forecastRows", "streamsPresent",
  "recent.oilAvg", "recent.gasAvg", "recent.waterAvg",
  "recent.oilTrend", "recent.gasTrend", "recent.waterTrend",
  "metrics.mape", "metrics.byStream.*.mape",
  "signals.declineRisk", "signals.volatility", "signals.dataQuality",
  "economics.pv", "economics.netback", "economics.breakeven", "economics.decision",
  "modelInfo.ageDays",
  "caveats",
];

const finite = (n) => Number.isFinite(n);
const fmtN = (n, d = 1) => (finite(n) ? n.toFixed(d) : "—");
const fmtUSD = (n) => (finite(n) ? `$${Math.round(n).toLocaleString("en-US")}` : "—");
const fmtPct = (n) => (finite(n) ? `${(n * 100).toFixed(0)}%` : "—");

function isOperational(assetType) {
  return assetType && assetType !== "producer" && assetType !== "unknown";
}

function decliningStreams(recent) {
  const out = [];
  for (const s of ["oil", "gas", "water"]) {
    if (recent?.[`${s}Trend`] === "down") out.push(s);
  }
  return out;
}

function highMapeStreams(metrics) {
  const out = [];
  const by = metrics?.byStream || {};
  for (const [s, m] of Object.entries(by)) {
    if (finite(m?.mape) && m.mape > 0.5) out.push(s);
  }
  return out;
}

// ── Headline logic ─────────────────────────────────────────────────────────
function buildHeadline(p) {
  if (isOperational(p.assetType)) {
    return `${p.leaseId}: operational asset (${p.assetType}) — not a primary producer.`;
  }
  if (p.status === "no_data" || (p.actualRows || 0) === 0) {
    return `${p.leaseId}: no actuals returned — staged backfill incomplete.`;
  }
  if ((p.forecastRows || 0) === 0) {
    return `${p.leaseId}: actuals only — no staged forecast yet.`;
  }
  const dq = p.signals?.dataQuality;
  if (dq === "poor") return `${p.leaseId}: thin data — directional only.`;

  const dec = p.signals?.declineRisk;
  const econDec = p.economics?.decision;

  if (econDec === "Operate" && dec === "high") {
    return `${p.leaseId}: profitable today, but a primary stream is declining materially.`;
  }
  if (econDec === "Operate" && dec === "medium") {
    return `${p.leaseId}: economic at default deck, mild decline trend to watch.`;
  }
  if (econDec === "Operate") {
    return `${p.leaseId}: economic at default deck; review for upside.`;
  }
  if (econDec === "Shut-in candidate") {
    return `${p.leaseId}: cashflow negative under default deck — evaluate shut-in.`;
  }
  if (econDec === "Monitor") {
    return `${p.leaseId}: marginal economics, near-term cashflow risk.`;
  }
  if (dec === "high") return `${p.leaseId}: investigate decline before optimizing spend.`;
  if (dec === "medium") return `${p.leaseId}: stable producer, mild decline to watch.`;
  return `${p.leaseId}: stable producer; review LOE for upside.`;
}

// ── Decision summary ───────────────────────────────────────────────────────
function buildDecisionSummary(p) {
  if (isOperational(p.assetType)) {
    return `Asset classified as ${p.assetType}. Excluded from producer ranking by default; useful for water-handling or surface-data diagnostics, not direct economic decisions.`;
  }
  if (p.status === "no_data" || (p.actualRows || 0) === 0) {
    return `Staged endpoint returned no actual production rows for ${p.leaseId}. The lease may be inactive in the snapshot or the upstream returned an empty payload. Refresh once new daily gauges are present.`;
  }
  if ((p.forecastRows || 0) === 0) {
    return `${p.actualRows} days of actuals are present, but the staged tables hold no forecast for ${p.leaseId}. Re-run pd_backfill to populate.`;
  }

  const parts = [];
  const o = p.recent?.oilAvg, g = p.recent?.gasAvg, w = p.recent?.waterAvg;
  const present = p.streamsPresent || [];
  if (present.length === 3 && finite(o) && finite(g)) {
    parts.push(`Three active streams (~${fmtN(o, 1)} bbl/d oil, ~${fmtN(g, 1)} mcf/d gas, ~${fmtN(w, 0)} bbl/d water).`);
  } else if (present.length > 0) {
    const bits = [];
    if (finite(o) && o > 0) bits.push(`oil ~${fmtN(o, 1)} bbl/d`);
    if (finite(g) && g > 0) bits.push(`gas ~${fmtN(g, 1)} mcf/d`);
    if (finite(w) && w > 0) bits.push(`water ~${fmtN(w, 0)} bbl/d`);
    parts.push(`Active streams: ${bits.join(", ")}.`);
  }

  if (p.economics?.decision) {
    const pv = p.economics?.pv;
    const be = p.economics?.breakeven;
    parts.push(
      `Default-deck econ: ${p.economics.decision}` +
      (finite(pv) ? `, PV ${fmtUSD(pv)}` : "") +
      (finite(be) ? `, breakeven $${fmtN(be, 2)}/bbl` : "") + ".",
    );
  }

  const declines = decliningStreams(p.recent);
  if (declines.length) {
    parts.push(`Declining trend on ${declines.join(", ")} (last 7d vs 30d).`);
  }

  if (p.signals?.declineRisk === "high") {
    parts.push("Decline risk is HIGH — investigate cause before committing capital.");
  }
  return parts.join(" ");
}

// ── Why it matters ─────────────────────────────────────────────────────────
function buildWhyItMatters(p) {
  if (isOperational(p.assetType)) {
    return "Including operational assets (injectors, meters, water transfers, SWDs) in producer rankings can mask real economic signals. Treat as supporting infrastructure.";
  }
  if (p.status === "no_data") {
    return "A producer that consistently returns no rows is either inactive, partitioned out by tenant filters, or missing from the latest snapshot — surface in the audit trail before any operator review.";
  }
  if ((p.forecastRows || 0) === 0) {
    return "Without a forecast, you cannot make forward-looking economic decisions; ranking is anchored to recent actuals only.";
  }
  const high = highMapeStreams(p.metrics);
  const declines = decliningStreams(p.recent);
  if (declines.length && p.economics?.decision === "Operate") {
    return `${p.leaseId} earns under current price assumptions but the trend on ${declines.join("/")} is downward. A small operational change today is far cheaper than a decision triggered by an econ-limit alert in 60 days.`;
  }
  if (high.length) {
    return `Train MAPE is high on ${high.join(", ")} — the p50 line is the wrong number to anchor decisions on. Use the p10/p90 bands and treat the recommendation as directional.`;
  }
  if (p.signals?.dataQuality === "poor") {
    return "Data density is poor; any economic conclusion is fragile until the dataset thickens.";
  }
  return "Stable producers are where margin optimization (LOE, water disposal, uptime) actually moves the dollar number. The decision tool surfaces them before they slip into decline.";
}

// ── Recommended action (mirrors deterministic.recommendation but resolves edge cases) ─
function buildRecommendedAction(p) {
  if (isOperational(p.assetType)) return "Exclude from producer ranking";
  if (p.status === "no_data" || (p.actualRows || 0) === 0) return "Refresh data";
  if ((p.forecastRows || 0) === 0) return "Re-run backfill";
  // forecastable
  const r = p.recommendation || "";
  if (r) return r;
  if (p.signals?.declineRisk === "high") return "Investigate Decline";
  if (p.economics?.decision === "Shut-in candidate") return "Evaluate Shut-in";
  return "Monitor";
}

// ── Risk flags ─────────────────────────────────────────────────────────────
function buildRiskFlags(p) {
  const flags = [];
  if (isOperational(p.assetType)) flags.push(`Operational asset: ${p.assetType}`);
  if (p.status === "no_data") flags.push("Upstream returned no data");
  if ((p.actualRows || 0) > 0 && (p.forecastRows || 0) === 0) flags.push("No staged forecast");
  if (p.modelInfo?.ageDays != null && p.modelInfo.ageDays > 30) {
    flags.push(`Forecast ${p.modelInfo.ageDays}d stale`);
  }
  if (p.signals?.dataQuality === "poor") flags.push("Poor data density");
  if (p.signals?.declineRisk === "high") flags.push("Decline risk: HIGH");
  if (p.signals?.volatility === "high") flags.push("Volatility: HIGH");
  for (const s of highMapeStreams(p.metrics)) {
    flags.push(`${s} train MAPE > 50%`);
  }
  if (p.economics?.decision === "Shut-in candidate") flags.push("Shut-in candidate at default deck");
  return flags.slice(0, 5);
}

// ── Operator next steps ────────────────────────────────────────────────────
function buildOperatorNextSteps(p) {
  if (isOperational(p.assetType)) {
    return [
      "Hide from producer ranking by default",
      "Use for water-handling diagnostics if relevant",
    ];
  }
  if (p.status === "no_data" || (p.actualRows || 0) === 0) {
    return [
      "Verify the lease is active in tenant LCS",
      "Refresh staged backfill from the latest service_testprod snapshot",
    ];
  }
  if ((p.forecastRows || 0) === 0) {
    return [
      "Re-run pd_backfill with this lease in scope",
      "Confirm the model didn't decline to fit (e.g. all-zero streams)",
    ];
  }
  const declines = decliningStreams(p.recent);
  if (declines.includes("oil")) {
    return [
      "Pull dyno card and fluid-level survey on the primary oil string",
      "Confirm pump fillage and check for scale / parted-rod indicators",
      "Run Bear-deck scenario in EconModal to size the downside",
    ];
  }
  if (declines.includes("gas") && (p.recent?.oilAvg || 0) > 0) {
    return [
      "Inspect gas-handling: separator, knockout, compressor cycling",
      "Confirm whether decline is reservoir or facility-side",
      "Check midstream curtailments / line pressure",
    ];
  }
  if (p.recommendation === "Optimize") {
    return [
      "Confirm recent LOE actuals match the default cost deck before acting",
      "Review uptime, lift settings, and chemical program for upside",
      "Stress-test in EconModal at the Bear price scenario",
    ];
  }
  if (p.recommendation === "Monitor") {
    return [
      "Continue current operating program",
      "Review again after the next backfill run",
      "Watch for rising water cut",
    ];
  }
  return [
    "Re-evaluate after next backfill",
    "Compare recent rates against type-curve expectation",
  ];
}

// ── Confidence rationale ───────────────────────────────────────────────────
function buildConfidenceRationale(p) {
  const reasons = [];
  const dq = p.signals?.dataQuality;
  if (dq === "excellent") reasons.push("data density is excellent");
  else if (dq === "good") reasons.push("data density is good");
  else if (dq === "fair") reasons.push("data density is only fair");
  else reasons.push("data density is poor");

  const high = highMapeStreams(p.metrics);
  if (high.length) reasons.push(`train MAPE > 50% on ${high.join(", ")}`);

  if (p.modelInfo?.ageDays != null) {
    if (p.modelInfo.ageDays > 30) reasons.push(`forecast is ${p.modelInfo.ageDays}d stale`);
    else if (p.modelInfo.ageDays > 7) reasons.push(`forecast is ${p.modelInfo.ageDays}d old`);
  }

  const dec = p.signals?.declineRisk;
  if (dec === "unknown") reasons.push("trend direction is unclear");

  return `Confidence is ${p.confidence || "unknown"}: ${reasons.join("; ")}.`;
}

// ── Caveats — pass-through from input + add econ-deck honesty ──────────────
function buildCaveats(p) {
  const c = [...(p.caveats || [])];
  c.push("Per-lease cost deck not loaded; economics are directional until operator cost inputs are applied.");
  return c;
}

// ── Demo narrative — one short sentence for the portfolio rollup ───────────
function buildDemoNarrative(p) {
  if (isOperational(p.assetType)) {
    return "Operational asset; appears in the discovery feed but is filtered out of producer demos.";
  }
  if (p.status === "no_data") {
    return "Discoverable but currently empty — useful as a real-data 'no-data' demo case.";
  }
  if ((p.forecastRows || 0) === 0) {
    return "Real actuals, no forecast — shows the gap between staged data and forecastable data.";
  }
  const econDec = p.economics?.decision;
  const dec = p.signals?.declineRisk;
  if (econDec === "Operate" && dec === "high") return "Profitable today, real declining trend — strongest 'why this tool matters' lease.";
  if (econDec === "Operate") return "Real producer earning under current deck; good baseline for optimization narrative.";
  if (econDec === "Shut-in candidate") return "Real shut-in candidate; useful for the 'tool actually says no' narrative.";
  return "Real-data lease with deterministic recommendation; honest demo material.";
}

/**
 * Build the deterministic commentary for a single lease.
 * Output matches COMMENTARY_CONTRACT — drop-in shape for a future LLM call.
 *
 * @param {object} profile
 * @returns {object}
 */
export function inferLeaseCommentary(profile) {
  const p = profile || {};
  return {
    source: "deterministic_local",
    model: null,
    generatedAt: new Date().toISOString(),
    contractVersion: COMMENTARY_CONTRACT_VERSION,
    inputFieldsUsed: INPUT_FIELDS,
    headline: buildHeadline(p),
    decisionSummary: buildDecisionSummary(p),
    whyItMatters: buildWhyItMatters(p),
    riskFlags: buildRiskFlags(p),
    recommendedAction: buildRecommendedAction(p),
    operatorNextSteps: buildOperatorNextSteps(p),
    confidenceRationale: buildConfidenceRationale(p),
    confidence: p.confidence || "low",
    caveats: buildCaveats(p),
    demoNarrative: buildDemoNarrative(p),
  };
}

/**
 * Build the input dict expected by inferLeaseCommentary from the LIVE
 * view-model + bundle entry + (optional) EconModal econ result. The cockpit
 * uses this when displaying inference for the currently-selected lease so
 * the commentary always reflects what the user is actually looking at.
 *
 * @param {object} args
 * @param {object} args.bundleEntry  - one entry from src/data/lcsStagedLeases.json
 * @param {object} [args.viewModel]   - optional output of buildLcsViewModel
 * @param {object} [args.econResult]  - optional output of computeEconWindow
 */
export function profileFromLive({ bundleEntry, viewModel, econResult } = {}) {
  const b = bundleEntry || {};
  const vm = viewModel || null;
  const er = econResult || null;

  const recent = {
    oilAvg: b.recentOilAvg ?? null,
    gasAvg: b.recentGasAvg ?? null,
    waterAvg: b.recentWaterAvg ?? null,
    oilTrend: undefined,
    gasTrend: undefined,
    waterTrend: undefined,
  };
  if (vm) {
    for (const s of vm.streams || []) {
      const trend = s.actual?.trendPct;
      const label =
        trend == null ? "unknown"
        : trend < -0.10 ? "down"
        : trend > 0.05 ? "up"
        : "flat";
      recent[`${s.name.toLowerCase()}Trend`] = label;
    }
  }

  const metrics = { mape: null, byStream: {} };
  if (vm) {
    for (const s of vm.streams || []) {
      if (s.metrics?.train?.mape != null) {
        metrics.byStream[s.name] = { mape: s.metrics.train.mape };
      }
    }
    const mapes = Object.values(metrics.byStream).map((m) => m.mape).filter((v) => Number.isFinite(v));
    if (mapes.length) metrics.mape = Math.min(...mapes);
  }

  const signals = {
    declineRisk: undefined,
    volatility: undefined,
    anomalyCount: 0,
    dataQuality: undefined,
  };
  if (vm) {
    const trend = vm.streams?.find((s) => s.name === "Oil")?.actual?.trendPct
      ?? vm.streams?.find((s) => s.name === "Gas")?.actual?.trendPct;
    if (trend == null) signals.declineRisk = "unknown";
    else if (trend < -0.30) signals.declineRisk = "high";
    else if (trend < -0.10) signals.declineRisk = "medium";
    else signals.declineRisk = "low";
    signals.anomalyCount = (vm.streams || []).reduce((s, x) => s + (x.anomalies?.length || 0), 0);
    signals.dataQuality = b.rowCount >= 90 ? "good"
      : b.rowCount >= 60 ? "good"
      : b.rowCount >= 30 ? "fair"
      : "poor";
  }

  const economics = er ? {
    pv: er.cumPV,
    cashFlow: er.cumCF,
    netback: er.netback,
    breakeven: er.beOilPV0 ?? null,  // not in computeEconWindow output by default
    decision: er.decision,
  } : null;

  return {
    leaseId: b.leaseId,
    leaseName: b.leaseName,
    rank: undefined,
    score: b.score,
    assetType: b.assetTypeHint,
    status: b.status,
    recommendation: b.recommendation,
    confidence: b.confidence,
    actualRows: b.rowCount,
    forecastRows: b.forecastRows,
    streamsPresent: b.streamsPresent,
    recent,
    metrics,
    signals,
    economics,
    modelInfo: vm?.modelInfo ? { ageDays: vm.modelInfo.staleness?.ageDays } : { ageDays: null },
    caveats: vm?.caveats || [],
  };
}

export default inferLeaseCommentary;
